import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement } from '../../core/types';
import { copySceneObjects } from '../../core/scene/editRouter';
import { mirrorMatrix } from '../cad/entityGeometry';

export class MirrorPickToolClass extends BaseTool {
    id = 'MIRROR_PICK';
    name = 'Mirror (Pick Axis)';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        // Requirement: User must have selected elements BEFORE activating this tool, 
        // OR we allow selection within the tool if nothing is selected.
        // BIM usually requires selection first, or enters a selection mode.
        // For simplicity, let's assume selection exists.

        const selection = this.context.appStore.selectedIds;
        if (selection.length === 0) {
            // Click-to-select: pick the element under the pointer first; the
            // next click picks the mirror axis.
            const id = this.findElementIdAtPoint(event);
            if (id) this.selectElement(id);
            return;
        }

        // User picks an edge/line to serve as the mirror axis
        const hit = this.getWallIntersection(event);
        if (!hit) return;

        const { wall } = hit;

        // Use wall centerline as axis
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);
        const axisLine = new THREE.Line3(start, end);

        this.mirrorElements(selection, axisLine);

        // Deactivate after operation
        this.context.appStore.setActiveTool('select');
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    private getWallIntersection(event: ThreeEvent<PointerEvent>) {
        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Walls');
        if (!hit) return null;
        return { wall: hit.element as WallElement, point: hit.point };
    }

    private mirrorElements(ids: string[], axis: THREE.Line3) {
        // Plane defined by axis and Z-up vector
        const axisDir = axis.end.clone().sub(axis.start).normalize();
        const normal = new THREE.Vector3(0, 0, 1).cross(axisDir).normalize();
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, axis.start);

        // B4 cross-workbench: foreign citizens mirror-COPY through the edit
        // router with the matrix equivalent of the same reflection.
        const foreignIds = ids.filter(id => !this.context!.bimStore.project.elements.has(id));
        if (foreignIds.length > 0) {
            copySceneObjects(
                foreignIds,
                mirrorMatrix([axis.start.x, axis.start.y], [axis.end.x, axis.end.y]),
                'Mirror'
            );
        }

        // Matrix for reflection
        // Construct reflection matrix manually or use math
        // Reflection across a plane: v' = v - 2 * (v . n + d) * n
        // We can compute new positions for start/end points of walls

        ids.forEach(id => {
            const element = this.context!.bimStore.project.elements.get(id);
            if (!element) return;

            // Clone element
            // We need a 'copyElement' or 'createSimilar' function in store, 
            // or we manually create a new one with transformed geometry.
            // Let's manually transform for now.

            if (element.category === 'Walls') {
                const wall = element as WallElement;
                const start = new THREE.Vector3(...wall.geometryData.start);
                const end = new THREE.Vector3(...wall.geometryData.end);

                // Reflect points
                const newStart = this.reflectPoint(start, plane);
                const newEnd = this.reflectPoint(end, plane);

                // Create new wall
                // We need to access the factory or store action. 
                // Since we don't have 'createWall' imported here easily without circular deps if we are not careful,
                // we can use a generic 'duplicateElement' if available, or just use the data.

                // For this refactor, I'll assume we can just add a new element with new ID.
                const newWall: WallElement = {
                    ...wall,
                    id: THREE.MathUtils.generateUUID(),
                    geometryData: {
                        ...wall.geometryData,
                        start: [newStart.x, newStart.y, newStart.z],
                        end: [newEnd.x, newEnd.y, newEnd.z]
                    }
                };

                this.context!.bimStore.addElement(newWall);
            }
        });
    }

    private reflectPoint(point: THREE.Vector3, plane: THREE.Plane): THREE.Vector3 {
        const p = point.clone();
        // distance to plane
        const d = plane.distanceToPoint(p);
        // v' = v - 2 * d * n
        return p.sub(plane.normal.clone().multiplyScalar(2 * d));
    }
}
